// Helpers for checklist step instructions, which are stored as Quill HTML
// since OTC-7 and as plain text on older steps and run snapshots.
import { sanitizeQuillHtml } from '@/util/quillSanitize'

const ALLOWED_TAGS = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'OL', 'UL', 'LI', 'A', 'SPAN', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE'])
const ALLOWED_ATTRS = new Set(['href', 'target', 'rel', 'style', 'class'])
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'META', 'LINK'])

export function isHtml(value) {
  return /<[a-z][^>]*>/i.test(String(value || ''))
}

function escapeText(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// Plain text to paragraphs. Blank lines split paragraphs, single newlines become <br>.
export function plainTextToHtml(text) {
  const s = String(text || '').replace(/\r\n?/g, '\n').trim()
  if (s === '') return ''
  return s.split(/\n{2,}/).map(p => '<p>' + escapeText(p).replace(/\n/g, '<br>') + '</p>').join('')
}

// Drops anything that can run code, keeps formatting, lists, and links.
export function stripUnsafeHtml(html) {
  if (typeof document === 'undefined') return String(html || '')
  const root = document.createElement('div')
  root.innerHTML = String(html || '')
  const walk = (node) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType !== 1) continue
      if (!ALLOWED_TAGS.has(child.tagName)) {
        if (DROP_TAGS.has(child.tagName)) {
          child.remove()
        } else {
          child.replaceWith(document.createTextNode(child.textContent || ''))
        }
        continue
      }
      for (const attr of Array.from(child.attributes)) {
        const name = attr.name.toLowerCase()
        const value = attr.value.trim().toLowerCase()
        if (!ALLOWED_ATTRS.has(name) || name.startsWith('on')) {
          child.removeAttribute(attr.name)
          continue
        }
        if (name === 'href' && /^(javascript|data|vbscript):/.test(value)) child.removeAttribute(attr.name)
      }
      if (child.tagName === 'A') {
        child.setAttribute('target', '_blank')
        child.setAttribute('rel', 'noopener noreferrer')
      }
      walk(child)
    }
  }
  walk(root)
  return root.innerHTML
}

// What the run page and details page bind to v-html.
export function renderInstructions(value) {
  if (!value) return ''
  return isHtml(value) ? stripUnsafeHtml(sanitizeQuillHtml(value)) : plainTextToHtml(value)
}

// Plain text preview for row cards and titles.
export function htmlToPlainText(value, max = 160) {
  if (!value) return ''
  let text = String(value)
  if (isHtml(text) && typeof document !== 'undefined') {
    const root = document.createElement('div')
    root.innerHTML = text.replace(/<\/(p|div|li|h[1-6])>/gi, ' ').replace(/<br\s*\/?>/gi, ' ')
    text = root.textContent || ''
  }
  text = text.replace(/\s+/g, ' ').trim()
  return text.length > max ? text.slice(0, max - 1) + '…' : text
}

// Quill reports an empty editor as <p><br></p>.
export function isEmptyHtml(value) {
  return htmlToPlainText(value, 100000) === ''
}

// The rich text editor writes its default text color inline: pure black in the
// light theme, pure white in the dark theme. Neither is a choice the author
// made, so when rendering on a fixed-color surface drop those two and let the
// surface's own color apply. Any other color came from the picker and stays.
const DEFAULT_TEXT_COLORS = new Set(['#000000', '#000', 'black', 'rgb(0, 0, 0)', 'rgb(0,0,0)',
  '#ffffff', '#fff', 'white', 'rgb(255, 255, 255)', 'rgb(255,255,255)'])

export function stripDefaultTextColors(html) {
  if (!html || typeof document === 'undefined') return html
  const root = document.createElement('div')
  root.innerHTML = String(html)
  root.querySelectorAll('[style*="color"]').forEach(el => {
    const color = (el.style.color || '').trim().toLowerCase()
    if (color && DEFAULT_TEXT_COLORS.has(color)) {
      el.style.removeProperty('color')
      if (!el.getAttribute('style')) el.removeAttribute('style')
    }
  })
  return root.innerHTML
}
